import { Grid, useEventCallback } from '@mui/material';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import {
  CommonSearch,
  FiltersFormPerson,
  IdentityCardVM,
  ResidencePermitVM,
  SearchByFiscalCodeVM,
  SubjectDataVM,
} from 'shell/view-models/search-anpr';
import { useNavigate } from 'react-router';
import { PmiButton } from 'shell/components/shared/button';
import FiltersSubjectData from './subject-data';
import FiltersResidencePermit from './residence-permit';
import FiltersIdentityCard from './identity-card';
import { StickyDiv } from '../../sticky-div';
import FilterSearchByFiscalCode from './search-by-fiscal-code';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import NationalSearch from './national-search';
import { FiltersPersonSearch } from 'shell/constants/filters-person-search';
import { initialValuesPerson } from 'shell/constants/initial-values-search';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { memo, useEffect, useState } from 'react';
import { defaultChoicePickerState } from '../../choice-date/constants';

interface FiltersProps {
  filterPersonForm: FormikProps<FiltersFormPerson>;
  isNationalSearch?: boolean;
  goBackUrl?: string;
  isOnlyNationalSearch?: boolean;
  onSubmit: (values: FiltersFormPerson) => void;
}

const FiltersComponent = ({
  filterPersonForm,
  isNationalSearch,
  goBackUrl = '/',
  isOnlyNationalSearch = false,
  onSubmit,
}: FiltersProps): React.ReactElement => {
  const navigate = useNavigate();
  const [loadingCall, setLoadingCall] = useState<boolean>(false);

  const { userProfile } = useUserProfile();

  const filterTable = useFilterTable<ResearchNotifications>();

  useEffect(() => {
    let subjectData: SubjectDataVM | undefined;

    if (
      filterTable.values?.searchPersonIDAnpr ||
      filterTable.values?.searchPersonName ||
      filterTable.values?.searchPersonSurname ||
      filterTable.values?.searchPersonDateBorn
    ) {
      subjectData = {
        ...initialValuesPerson.filters?.subjectData,
        name: filterTable.values?.searchPersonIDAnpr
          ? ''
          : filterTable.values?.searchPersonName || '',
        surname: filterTable.values?.searchPersonIDAnpr
          ? ''
          : filterTable.values?.searchPersonSurname || '',
        withoutName: false,
        withoutSurname: false,
        birthday: filterTable.values?.searchPersonIDAnpr
          ? null
          : filterTable.values?.searchPersonDateBorn || null,
        birthdayChoice: filterTable.values?.searchPersonIDAnpr
          ? undefined
          : filterTable.values?.searchPersonDateBorn
          ? defaultChoicePickerState
          : undefined,
        istatCode: '',
        anprId: filterTable.values?.searchPersonIDAnpr,
        municipalityId: '',
      };
    }

    const newValues: FiltersFormPerson = {
      ...(initialValuesPerson.filters || {}),
      subjectData,
      searchByFiscalCode: {
        ...initialValuesPerson.filters?.searchByFiscalCode,
      },
      commonSearch: {
        ...initialValuesPerson?.filters?.commonSearch,
        isNationalResearch: isNationalSearch || undefined,
        municipalityOfResidence: !isNationalSearch
          ? {
              id: userProfile.datiLogin?.codiceIstat,
              description: userProfile.datiLogin?.nomeComune || '',
              province: userProfile.datiLogin?.siglaProvincia || '',
            }
          : null,
      },
      openedAccordion: FiltersPersonSearch.SubjectData,
    };

    if (filterTable.values?.searchPersonIDAnpr && subjectData && !loadingCall) {
      onSubmit(newValues);
      setLoadingCall(true);
      return;
    }

    if (subjectData) {
      filterPersonForm.setValues(newValues);
    }
  }, [filterTable.values]);

  // handle accordion subject data
  const handleChangeSubjectData = useEventCallback(
    (key: keyof SubjectDataVM, value: SubjectDataVM[keyof SubjectDataVM]) => {
      filterPersonForm.setFieldValue('subjectData', {
        ...(filterPersonForm.values.subjectData || {}),
        [key]: value,
      });
    }
  );

  // handle accordion subject data
  const handleChangeFieldsSubjectData = useEventCallback((values: SubjectDataVM) => {
    filterPersonForm.setFieldValue('subjectData', {
      ...(filterPersonForm.values.subjectData || {}),
      ...values,
    });
  });

  // handle accordion subject data
  const handleChangeFieldsSearchByFiscalCode = useEventCallback((values: SearchByFiscalCodeVM) => {
    filterPersonForm.setFieldValue('searchByFiscalCode', {
      ...(filterPersonForm.values.searchByFiscalCode || {}),
      ...values,
    });
  });

  // handle accordion identity card data
  const handleChangeIdentityCard = useEventCallback(
    (key: keyof IdentityCardVM, value: IdentityCardVM[keyof IdentityCardVM]) => {
      filterPersonForm.setFieldValue('identityCard', {
        ...(filterPersonForm.values.identityCard || {}),
        [key]: value,
      });
    }
  );

  // handle accordion residence permit data
  const handleChangeResidencePermit = useEventCallback(
    (key: keyof ResidencePermitVM, value: ResidencePermitVM[keyof ResidencePermitVM]) => {
      filterPersonForm.setFieldValue('residencePermit', {
        ...(filterPersonForm.values.residencePermit || {}),
        [key]: value,
      });
    }
  );
  //handle accordina search by fiscal code
  const handleChangeSearchByFiscalCode = useEventCallback(
    (key: keyof SearchByFiscalCodeVM, value: SearchByFiscalCodeVM[keyof SearchByFiscalCodeVM]) => {
      filterPersonForm.setFieldValue('searchByFiscalCode', {
        ...(filterPersonForm.values.searchByFiscalCode || {}),
        [key]: value,
      });
    }
  );

  const onOpenAccordion = useEventCallback((value: FiltersPersonSearch) => {
    filterPersonForm.setValues({
      ...(initialValuesPerson.filters || {}),
      subjectData: {
        ...initialValuesPerson.filters?.subjectData,
        name: '',
        surname: '',
        withoutName: false,
        withoutSurname: false,
        istatCode: '',
        anprId: '',
        municipalityId: '',
      },
      searchByFiscalCode: {
        ...initialValuesPerson.filters?.searchByFiscalCode,
      },
      commonSearch: {
        ...initialValuesPerson?.filters?.commonSearch,
        isNationalResearch: isNationalSearch || undefined,
        municipalityOfResidence: !isNationalSearch
          ? {
              id: userProfile.datiLogin?.codiceIstat,
              description: userProfile.datiLogin?.nomeComune || '',
              province: userProfile.datiLogin?.siglaProvincia || '',
            }
          : null,
      },
      openedAccordion: value,
    });
  });
  //handle accordina search for commonSearch NationalSearch
  const handleChangeCommonSearch = useEventCallback(
    (key: keyof CommonSearch, value: CommonSearch[keyof CommonSearch]) => {
      filterPersonForm.setFieldValue('commonSearch', {
        ...(filterPersonForm.values.commonSearch || {}),
        [key]: value,
      });
    }
  );
  //handle onChangeFields for Common national Search

  const handleChangeFieldsCommonSearch = useEventCallback((values: CommonSearch) => {
    filterPersonForm.setFieldValue('commonSearch', {
      ...(filterPersonForm.values.commonSearch || {}),
      ...values,
    });
  });
  return (
    <form onSubmit={filterPersonForm.handleSubmit} onReset={filterPersonForm.handleReset}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StickyDiv>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="contained" color="cdmBrandThird" type="submit" fullWidth>
                Cerca
              </PmiButton>
            </div>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="outlined" color="cdmBrandThird" type="reset" fullWidth>
                Pulisci
              </PmiButton>
            </div>
            <div className="pmi-d-block">
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => navigate(goBackUrl)}
              >
                Indietro
              </PmiButton>
            </div>
          </StickyDiv>
        </Grid>
        <Grid item xs={12} md={9}>
          <NationalSearch
            isNationalResearch={filterPersonForm?.values?.commonSearch?.isNationalResearch}
            isOnlyNationalSearch={isOnlyNationalSearch}
            provinceOfResidence={filterPersonForm?.values?.commonSearch?.provinceOfResidence}
            municipalityOfResidence={
              filterPersonForm?.values?.commonSearch?.municipalityOfResidence
            }
            errorMunicipalityOfResidence={
              (filterPersonForm?.errors?.commonSearch as FormikErrors<CommonSearch>)
                ?.municipalityOfResidence as FormikErrors<MunicipalityAnprVM>
            }
            touchedMunicipalityOfResidence={
              (filterPersonForm?.touched?.commonSearch as unknown as FormikTouched<CommonSearch>)
                ?.municipalityOfResidence as unknown as FormikTouched<MunicipalityAnprVM>
            }
            onChange={handleChangeCommonSearch}
            onChangeValues={handleChangeFieldsCommonSearch}
          />
          <FilterSearchByFiscalCode
            open={filterPersonForm?.values?.openedAccordion === FiltersPersonSearch.FiscalCode}
            onOpenAccordion={onOpenAccordion}
            values={filterPersonForm?.values?.searchByFiscalCode}
            onChange={handleChangeSearchByFiscalCode}
            touched={
              filterPersonForm.touched
                .searchByFiscalCode as unknown as FormikTouched<SearchByFiscalCodeVM>
            }
            errors={
              filterPersonForm?.errors?.searchByFiscalCode as FormikErrors<SearchByFiscalCodeVM>
            }
            onChangeValues={handleChangeFieldsSearchByFiscalCode}
          />
          <FiltersSubjectData
            onOpenAccordion={onOpenAccordion}
            open={filterPersonForm?.values?.openedAccordion === FiltersPersonSearch.SubjectData}
            values={filterPersonForm.values.subjectData}
            onChange={handleChangeSubjectData}
            errors={filterPersonForm.errors.subjectData as FormikErrors<SubjectDataVM>}
            touched={
              filterPersonForm.touched.subjectData as unknown as FormikTouched<SubjectDataVM>
            }
            onChangeValues={handleChangeFieldsSubjectData}
          />
          <FiltersIdentityCard
            onOpenAccordion={onOpenAccordion}
            open={filterPersonForm?.values?.openedAccordion === FiltersPersonSearch.IdentityCard}
            values={filterPersonForm.values.identityCard}
            onChange={handleChangeIdentityCard}
            errors={filterPersonForm.errors.identityCard as FormikErrors<IdentityCardVM>}
            touched={
              filterPersonForm.touched.identityCard as unknown as FormikTouched<IdentityCardVM>
            }
          />
          <FiltersResidencePermit
            onOpenAccordion={onOpenAccordion}
            open={filterPersonForm?.values?.openedAccordion === FiltersPersonSearch.ResidencePermit}
            values={filterPersonForm.values.residencePermit}
            onChange={handleChangeResidencePermit}
            errors={filterPersonForm.errors.residencePermit as FormikErrors<ResidencePermitVM>}
            touched={
              filterPersonForm.touched
                .residencePermit as unknown as FormikTouched<ResidencePermitVM>
            }
          />
        </Grid>
      </Grid>
    </form>
  );
};

FiltersComponent.displayName = 'Filters Person component';

const Filters = memo(FiltersComponent);

export default Filters;
